/**
 * @file ClientDashboard.jsx
 * @description Main application interface with role-specific features
 * 
 * @section Responsibilities
 * - Manage role-based views
 * - Handle data aggregation
 * - Control real-time updates
 * - Manage user preferences
 * 
 * @section Dependencies
 * - Role Management: Access control
 * - Data Services: Information handling
 * - State Management: Global state
 * 
 * @section Integration Points
 * - Data API: Information retrieval
 * - Role System: Access control
 * - Notification System: Real-time updates
 * 
 * @section State Management
 * - dashboardData: Aggregated information
 * - loadingState: Data fetching
 * - errorState: Error handling
 * 
 * @section Events
 * - onDataUpdate: Data changes
 * - onError: Error handling
 * - onPreferenceChange: Settings updates
 * 
 * @section Props
 * @prop {String} userRole - Current user role
 * @prop {Object} permissions - User permissions
 */

import React, { useCallback, useEffect, useState } from "react";
import SkinPage from "../../SkinPage";
import PersonalPortfolio from "../../widgets/portfolio/PersonalPortfolio";
import PortfolioAllocation from "../../widgets/portfolio/PortfolioAllocation";
import Request from "../../../../utils/fetchUtils";
import { getFromSession } from "../../../../utils/sessionUtils";
import OnboardingStatus from "./OnboardingStatus";
import StrategiesGainLoss from "../../widgets/portfolio/StrategiesGainLoss";
import Widget from "../../widgets/Widget";
import { useParams, useLocation } from "react-router";
import Positions from "../../widgets/trading/Positions";
import Loader from "../../widgets/Loader";
import TradierImport from "../../widgets/profile/TradierImport";

function ClientDashboard() {
  const { id } = useParams();
  const location = useLocation();
  const customer = location.state || {};
  const sessionRole = getFromSession("role");
  const isCustomer = sessionRole === "customer";
  const userApplications = getFromSession("applications") || [];
  const isImportedUser = userApplications.includes("imported-user");
  const brokerAccounts = isCustomer
    ? getFromSession("brokerAccounts")
    : customer
      ? customer.brokerAccounts
      : [];
  const Userkey = isCustomer ? getFromSession("SK") : id;
  const tradierAccount =
    brokerAccounts?.find((acc) => acc.provider === "tradier") || {};
  const [isLoading, setLoading] = useState(false);
  const [data, setData] = useState({});
  const [strategies, setStrategies] = useState([]);
  const [positions, setPositions] = useState([]);
  const [fullId, setFullId] = useState(null);

  const advisor = {
    name: getFromSession("advisorName") ?? "",
    phone: getFromSession("advisorPhone") ?? "",
  };
  useEffect(() => {
    const hashPart = window.location.hash;
    if (hashPart) {
      setFullId(`${id}${hashPart}`);
    }
  }, [id]);
  
  const filterStrategiesByCustomer = (strategies, customerKey) => {
    return strategies.filter(strategy =>
      strategy.customers?.some(customer =>
        customer.userSk?.includes(customerKey) || customer.userId === customerKey
      )
    );
  };

  const fetchDashboard = useCallback(async (queryParams = "") => {
    // Don't fetch dashboard data for imported users without Tradier account
    if (isImportedUser && !tradierAccount.id) {
      return;
    }

    setLoading(true);
    const accountQuery = tradierAccount.id
      ? "accountId=" + tradierAccount.id
      : "a=a";
    const res = await Request(
      `/trading/dashboard?${accountQuery}&${queryParams}`,
      {},
      "GET",
      { Userkey: fullId ?? Userkey }
    );

    setLoading(false);
    if (res.ok) {
      setData(res.data);
      if (res.data && res.data.positions) {
        setPositions(res.data.positions);
      }
    }
  }, 
  
  
  [tradierAccount.id, fullId, Userkey, isImportedUser]);


  const getDashboardData = useCallback(async () => {
    // Don't fetch data for imported users without Tradier account
    if (isImportedUser && !tradierAccount.id) {
      return;
    }

    fetchDashboard();
    if (tradierAccount?.id) {
      const resStrat = await Request(`/trading/strategy`, {}, "GET", {
        Userkey: fullId ?? Userkey,
      });
      if (resStrat.ok) {

        const filteredStrategies = filterStrategiesByCustomer(
          resStrat.data,
          fullId ?? Userkey
        );
        setStrategies(filteredStrategies);
      }
    }
  }, [tradierAccount?.id, fullId, Userkey, fetchDashboard, isImportedUser]);
  useEffect(() => {
    if ((fullId && fullId.includes("#")) || !fullId) {
      getDashboardData();
    }
  }, [fullId, tradierAccount?.id, getDashboardData]); return (
    <SkinPage>
      {isLoading && <Loader />}
      {!isCustomer ||(isImportedUser && tradierAccount.id)||(advisor.name.length > 0 && tradierAccount.id) ? (
        <>
          <PersonalPortfolio
            onFilter={fetchDashboard}
            width={"full-width"}
            data={data}
            loading={isLoading}
            account={tradierAccount}
          />
          <div className="widget-row">
            <div className="widget-column">
              <PortfolioAllocation
                width={"full-width"}
                data={data}
                loading={isLoading}
                account={tradierAccount}
              />
              {/* <Help width={"full-width"} /> */}
            </div>
            <div className="widget-column">
              <StrategiesGainLoss
                width={"full-width"}
                data={strategies}
                loading={isLoading}
                account={tradierAccount}
                strategies={strategies}
              />
            </div>
          </div>

          <Widget name={"positions-history"} width={"full-width"}>
            <h3 className="description" style={{ color: "#eeeeee" }}>
              Open positions
            </h3>
            <Positions positions={positions} />
          </Widget>
        </>
      ) : isCustomer &&
        (isImportedUser || data.brokerLinkStatus === "needs_authorization") &&
        !tradierAccount.id ? (
        // Shown both for imported users without a Tradier account and for any
        // customer whose application was approved but who hasn't yet
        // authorized the OAuth connection (ADFE-150) — not gated on
        // isImportedUser alone anymore.
        <Widget name={"tradier-import-required"} width={"full-width"}>
          <div style={{ textAlign: "center", padding: "40px" }}>
            <h2 style={{ color: "#95b2c5", marginBottom: "20px" }}>
              {isImportedUser
                ? "Welcome! Import Your Tradier Account"
                : "Your Tradier Application Was Approved"}
            </h2>
            <p style={{ color: "#8e8e93", marginBottom: "30px", fontSize: "16px" }}>
              To get started, please connect your Tradier broker account first.
              This will allow you to view your portfolio, positions, and trading data.
            </p>
            <TradierImport
              showBrokerAccounts={false}
            />
          </div>
        </Widget>
      ) : (
        <OnboardingStatus
          advisor={advisor}
          application={data.application?.body || {}}
        />
      )}
    </SkinPage>
  );
}

export default ClientDashboard;
